import React, { useState, useEffect, useContext } from 'react';
import {
	Box,
	Button,
	Card,
	CardContent,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Chip,
	IconButton,
	Tooltip,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	TextField,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	CircularProgress,
	Alert,
	Grid,
	Autocomplete,
} from '@mui/material';
import {
	Edit as EditIcon,
	Send as SendIcon,
	Refresh as RefreshIcon,
	Close as CloseIcon,
	Add as AddIcon,
	BarChart as BarChartIcon,
	Delete as DeleteIcon,
} from '@mui/icons-material';
import AuthContext from '../../store/auth-context';
import SnackbarContext from '../../store/snackbar-context';
import OlympicsContext from '../../store/olympics-context';
import EmailCampaignRecipient from '../../model/EmailCampaignRecipient';
import CampaignStatisticsDialog from './CampaignStatisticsDialog';
import {
	getCampaignRecipients,
	getMyRecipients,
	updateRecipientComment,
	resendToRecipients,
	addManualRecipient,
	getAvailableRecipients,
	deleteRecipient,
} from '../../api/email-campaign-api';

interface EmailCampaignRecipientsTableProps {
	campaignId: number;
	campaignName: string;
	onClose: () => void;
}

const EmailCampaignRecipientsTable: React.FC<EmailCampaignRecipientsTableProps> = ({
	campaignId,
	campaignName,
	onClose,
}) => {
	const { token, role } = useContext(AuthContext);
	const { setMsg } = useContext(SnackbarContext);
	const { districtFilterId, searchFilterValue, refreshTables } = useContext(OlympicsContext);

	const [recipients, setRecipients] = useState<EmailCampaignRecipient[]>([]);
	const [displayedRecipients, setDisplayedRecipients] = useState<EmailCampaignRecipient[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [statusFilter, setStatusFilter] = useState<string>('all');
	const [editingComment, setEditingComment] = useState<{ id: number; comment: string } | null>(null);
	const [addManualDialogOpen, setAddManualDialogOpen] = useState(false);
	const [statisticsDialogOpen, setStatisticsDialogOpen] = useState(false);

	const isAdmin = role === 'administrator';

	useEffect(() => {
		downloadData();
	}, [campaignId, token]);

	useEffect(() => {
		filterRecipients();
	}, [recipients, statusFilter, districtFilterId, searchFilterValue]);

	const downloadData = async () => {
		if (!token) return;

		try {
			setIsLoading(true);
			let response;

			if (isAdmin) {
				response = await getCampaignRecipients(token, campaignId, {
					district_id: districtFilterId !== -1 ? districtFilterId : undefined,
				});
			} else {
				response = await getMyRecipients(token, campaignId);
			}

			if (response.status === 200) {
				const recipientsData = response.data.map((d: any) => EmailCampaignRecipient.fromApiResponse(d));
				setRecipients(recipientsData);
			} else {
				setMsg({ msg: response.data?.message || 'Błąd podczas pobierania odbiorców' });
			}
		} catch (exception: any) {
			console.error(exception);
			if (exception.response?.status === 401 || exception.response?.status === 403) {
				setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
			} else {
				setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
			}
		} finally {
			setIsLoading(false);
		}
	};

	const filterRecipients = () => {
		let filtered = [...recipients];

		// District filter - ONLY for admins (secretary already filtered by backend)
		if (isAdmin && districtFilterId !== -1) {
			filtered = filtered.filter((recipient) => recipient.districtId === districtFilterId);
		}

		// Status filter
		if (statusFilter !== 'all') {
			filtered = filtered.filter((recipient) => {
				switch (statusFilter) {
					case 'sent':
						return recipient.emailSentAt;
					case 'clicked':
						return recipient.linkClickedAt;
					case 'not_clicked':
						return recipient.emailSentAt && !recipient.linkClickedAt;
					case 'error':
						return recipient.emailError;
					case 'not_verified':
						return (
							recipient.emailSentAt &&
							(!recipient.linkClickedAt || !recipient.passwordVerifiedAt || !recipient.codeEnteredAt)
						);
					case 'verified':
						return (
							recipient.emailSentAt &&
							recipient.linkClickedAt &&
							recipient.passwordVerifiedAt &&
							recipient.codeEnteredAt
						);
					default:
						return true;
				}
			});
		}

		// Search filter
		if (searchFilterValue) {
			filtered = filtered.filter((recipient) => {
				const searchLower = searchFilterValue.toLowerCase();
				return (
					recipient.recipientName.toLowerCase().includes(searchLower) ||
					recipient.recipientEmail.toLowerCase().includes(searchLower) ||
					recipient.districtName.toLowerCase().includes(searchLower)
				);
			});
		}

		setDisplayedRecipients(filtered);
	};

	const handleUpdateComment = async (recipientId: number, comment: string) => {
		if (!token) return;

		try {
			const response = await updateRecipientComment(token, recipientId, comment);
			if (response.status === 200) {
				setMsg({ msg: 'Komentarz został zaktualizowany' });
				downloadData(); // Refresh data
				setEditingComment(null);
			} else {
				setMsg({ msg: response.data?.message || 'Błąd podczas aktualizacji komentarza' });
			}
		} catch (exception: any) {
			console.error(exception);
			setMsg({ msg: 'Błąd podczas aktualizacji komentarza' });
		}
	};

	const handleResend = async (recipientIds: number[]) => {
		if (!token) return;

		try {
			const response = await resendToRecipients(token, campaignId, recipientIds);
			if (response.status === 200 && response.data) {
				// Użyj message z API lub stwórz własny
				const message = response.data.message || `Wysłano ponownie ${response.data.sent} wiadomości`;
				setMsg({ msg: message, severity: 'success' });
				downloadData(); // Refresh data
			} else {
				setMsg({ msg: response.data?.message || 'Błąd podczas ponownego wysyłania', severity: 'error' });
			}
		} catch (exception: any) {
			console.error(exception);
			setMsg({ msg: 'Błąd podczas ponownego wysyłania' });
		}
	};

	const handleDeleteRecipient = async (recipientId: number) => {
		if (!token) return;

		if (!window.confirm('Czy na pewno chcesz usunąć tego odbiorcę z wysyłki?')) {
			return;
		}

		try {
			const response = await deleteRecipient(token, recipientId);
			if (response.status === 200) {
				setMsg({ msg: 'Odbiorca został usunięty', severity: 'success' });
				downloadData();
				refreshTables();
			} else {
				setMsg({ msg: response.data?.message || 'Błąd podczas usuwania odbiorcy', severity: 'error' });
			}
		} catch (exception: any) {
			console.error(exception);
			setMsg({ msg: 'Błąd podczas usuwania odbiorcy', severity: 'error' });
		}
	};

	const handleRefresh = () => {
		downloadData();
	};

	const getRecipientTypeIcon = (type: string) => {
		switch (type) {
			case 'teacher':
				return '👨‍🏫';
			case 'secretary':
				return '📋';
			case 'student':
				return '🎓';
			default:
				return '👤';
		}
	};

	const getStatusChip = (recipient: EmailCampaignRecipient) => {
		if (!recipient.emailSentAt) {
			return (
				<Chip
					label='Nie wysłano'
					color='default'
					size='small'
				/>
			);
		}

		if (recipient.codeEnteredAt) {
			return (
				<Chip
					label={recipient.codeIsValid ? 'Zweryfikowany' : 'Błędny kod'}
					color={recipient.codeIsValid ? 'success' : 'error'}
					size='small'
				/>
			);
		}

		if (recipient.passwordVerifiedAt) {
			return (
				<Chip
					label='Hasło OK'
					color='info'
					size='small'
				/>
			);
		}

		if (recipient.linkClickedAt) {
			return (
				<Chip
					label='Link kliknięty'
					color='warning'
					size='small'
				/>
			);
		}

		return (
			<Chip
				label='Wysłano'
				color='primary'
				size='small'
			/>
		);
	};

	const formatDateTime = (date: Date | undefined) => {
		if (!date) return '-';
		return new Date(date).toLocaleString('pl-PL');
	};

	if (isLoading) {
		return (
			<Box
				display='flex'
				justifyContent='center'
				alignItems='center'
				minHeight='400px'
			>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box>
			{/* Header with filters */}
			<Box
				display='flex'
				gap={2}
				mb={3}
				alignItems='center'
				flexWrap='wrap'
			>
				<FormControl
					size='small'
					sx={{ minWidth: 150 }}
				>
					<InputLabel>Status</InputLabel>
					<Select
						value={statusFilter}
						label='Status'
						onChange={(e) => setStatusFilter(e.target.value)}
					>
						<MenuItem value='all'>Wszystkie</MenuItem>
						<MenuItem value='sent'>Wysłane</MenuItem>
						<MenuItem value='clicked'>Kliknięte</MenuItem>
						<MenuItem value='not_clicked'>Nie kliknięte</MenuItem>
						<MenuItem value='error'>Błąd wysyłki</MenuItem>
						<MenuItem value='not_verified'>Niezweryfikowane</MenuItem>
						<MenuItem value='verified'>Zweryfikowane</MenuItem>
					</Select>
				</FormControl>

				<Button
					variant='outlined'
					startIcon={<BarChartIcon />}
					onClick={() => setStatisticsDialogOpen(true)}
				>
					Statystyki
				</Button>

				<Button
					variant='outlined'
					startIcon={<RefreshIcon />}
					onClick={handleRefresh}
				>
					Odśwież
				</Button>

				{isAdmin && (
					<Button
						variant='contained'
						startIcon={<AddIcon />}
						onClick={() => setAddManualDialogOpen(true)}
						sx={{ ml: 'auto' }}
					>
						Dodaj odbiorcę
					</Button>
				)}

				<IconButton
					onClick={onClose}
					sx={{ ml: 1 }}
				>
					<CloseIcon />
				</IconButton>
			</Box>

			{/* Recipients Table */}
			<TableContainer
				component={Paper}
				sx={{
					maxHeight: 'calc(100vh - 290px)',
					overflowX: 'auto',
					maxWidth: 'calc(100% - 30px)',
				}}
			>
				<Table stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell>Typ</TableCell>
							<TableCell>Imię i nazwisko</TableCell>
							<TableCell>Email</TableCell>
							<TableCell>Okręg</TableCell>
							<TableCell>Ręczny</TableCell>
							<TableCell>Wysłano</TableCell>
							<TableCell>Kliknięcie</TableCell>
							<TableCell>Hasło</TableCell>
							<TableCell>Kod</TableCell>
							<TableCell>Komentarz</TableCell>
							{isAdmin && <TableCell align='center'>Akcje</TableCell>}
						</TableRow>
					</TableHead>
					<TableBody>
						{displayedRecipients.map((recipient) => (
							<TableRow
								key={recipient.id}
								hover
							>
								<TableCell>
									<Typography variant='body2'>{getRecipientTypeIcon(recipient.recipientType)}</Typography>
								</TableCell>
								<TableCell>
									<Typography
										variant='body2'
										fontWeight='medium'
									>
										{recipient.recipientName}
									</Typography>
								</TableCell>
								<TableCell>
									<Typography variant='body2'>{recipient.recipientEmail}</Typography>
								</TableCell>
								<TableCell>
									<Typography variant='body2'>{recipient.districtName}</Typography>
								</TableCell>
								<TableCell>
									{recipient.isManualAddition && (
										<Chip
											label='✓'
											color='info'
											size='small'
										/>
									)}
								</TableCell>
								<TableCell>
									<Typography variant='body2'>{formatDateTime(recipient.emailSentAt)}</Typography>
								</TableCell>
								<TableCell>
									<Typography variant='body2'>
										{recipient.linkClickedAt ? formatDateTime(recipient.linkClickedAt) : 'n/d'}
									</Typography>
								</TableCell>
								<TableCell>
									<Typography variant='body2'>
										{recipient.passwordVerifiedAt ? formatDateTime(recipient.passwordVerifiedAt) : 'n/d'}
									</Typography>
								</TableCell>
								<TableCell>
									<Box>
										<Typography variant='body2'>
											{recipient.codeEnteredAt ? formatDateTime(recipient.codeEnteredAt) : 'n/d'}
										</Typography>
										{recipient.codeEnteredValue && (
											<Typography
												variant='caption'
												color='text.secondary'
											>
												Kod: {recipient.codeEnteredValue} {recipient.codeIsValid ? '✓' : '✗'}
											</Typography>
										)}
									</Box>
								</TableCell>
								<TableCell>
									<Box
										display='flex'
										alignItems='center'
										gap={1}
									>
										<Typography
											variant='body2'
											sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}
										>
											{recipient.adminComment || '-'}
										</Typography>
										<Tooltip title='Edytuj komentarz'>
											<IconButton
												size='small'
												onClick={() => setEditingComment({ id: recipient.id, comment: recipient.adminComment || '' })}
											>
												<EditIcon fontSize='small' />
											</IconButton>
										</Tooltip>
									</Box>
								</TableCell>
								{isAdmin && (
									<TableCell align='center'>
										<Box
											display='flex'
											gap={1}
											justifyContent='center'
										>
											<Tooltip title='Wyślij ponownie'>
												<IconButton
													size='small'
													onClick={() => handleResend([recipient.id])}
													disabled={!recipient.emailSentAt}
												>
													<SendIcon />
												</IconButton>
											</Tooltip>
											<Tooltip
												title={
													recipient.emailSentAt && !recipient.isManualAddition
														? 'Nie można usunąć: wysyłka już wykonana'
														: 'Usuń odbiorcę'
												}
											>
												<span>
													<IconButton
														size='small'
														onClick={() => handleDeleteRecipient(recipient.id)}
														disabled={!!recipient.emailSentAt && !recipient.isManualAddition}
														color='error'
													>
														<DeleteIcon />
													</IconButton>
												</span>
											</Tooltip>
										</Box>
									</TableCell>
								)}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			{displayedRecipients.length === 0 && (
				<Alert
					severity='info'
					sx={{ mt: 2 }}
				>
					Brak odbiorców spełniających kryteria wyszukiwania.
				</Alert>
			)}

			{/* Edit Comment Dialog */}
			<Dialog
				open={!!editingComment}
				onClose={() => setEditingComment(null)}
				maxWidth='sm'
				fullWidth
			>
				<DialogTitle>Edytuj komentarz</DialogTitle>
				<DialogContent>
					<TextField
						label='Komentarz'
						value={editingComment?.comment || ''}
						onChange={(e) => setEditingComment((prev) => (prev ? { ...prev, comment: e.target.value } : null))}
						fullWidth
						multiline
						rows={3}
						sx={{ mt: 1 }}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setEditingComment(null)}>Anuluj</Button>
					<Button
						onClick={() => editingComment && handleUpdateComment(editingComment.id, editingComment.comment)}
						variant='contained'
					>
						Zapisz
					</Button>
				</DialogActions>
			</Dialog>

			{/* Add Manual Recipient Dialog */}
			<AddManualRecipientDialog
				open={addManualDialogOpen}
				onClose={() => setAddManualDialogOpen(false)}
				campaignId={campaignId}
				onSuccess={() => {
					setAddManualDialogOpen(false);
					downloadData();
					refreshTables();
				}}
			/>

			{/* Statistics Dialog */}
			<CampaignStatisticsDialog
				campaignId={campaignId}
				campaignName={campaignName}
				open={statisticsDialogOpen}
				onClose={() => setStatisticsDialogOpen(false)}
			/>
		</Box>
	);
};

// Add Manual Recipient Dialog Component
interface AddManualRecipientDialogProps {
	open: boolean;
	onClose: () => void;
	campaignId: number;
	onSuccess: () => void;
}

const AddManualRecipientDialog: React.FC<AddManualRecipientDialogProps> = ({
	open,
	onClose,
	campaignId,
	onSuccess,
}) => {
	const { token } = useContext(AuthContext);
	const { setMsg } = useContext(SnackbarContext);

	const [recipientType, setRecipientType] = useState<'teacher' | 'secretary' | 'student'>('teacher');
	const [availableRecipients, setAvailableRecipients] = useState<any[]>([]);
	const [selectedRecipient, setSelectedRecipient] = useState<any | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingRecipients, setIsLoadingRecipients] = useState(false);

	useEffect(() => {
		if (open && token) {
			loadAvailableRecipients();
		}
	}, [open, recipientType, token]);

	const loadAvailableRecipients = async () => {
		if (!token) return;

		setIsLoadingRecipients(true);
		try {
			const response = await getAvailableRecipients(token, campaignId, recipientType);
			if (response.data && response.data.recipients) {
				setAvailableRecipients(response.data.recipients);
			}
		} catch (err) {
			console.error('Error loading available recipients:', err);
			setMsg({ msg: 'Błąd podczas ładowania dostępnych odbiorców', severity: 'error' });
		} finally {
			setIsLoadingRecipients(false);
		}
	};

	const handleSubmit = async () => {
		if (!token || !selectedRecipient) return;

		try {
			setIsLoading(true);

			const response = await addManualRecipient(token, campaignId, {
				recipient_type: selectedRecipient.type,
				recipient_id: selectedRecipient.id,
				recipient_email: selectedRecipient.email,
				recipient_name: selectedRecipient.name,
				district_id: selectedRecipient.district_id,
			});

			if (response.status === 201) {
				setMsg({ msg: 'Odbiorca został dodany', severity: 'success' });
				onSuccess();
				handleClose();
			} else {
				setMsg({ msg: response.data?.message || 'Błąd podczas dodawania odbiorcy', severity: 'error' });
			}
		} catch (exception: any) {
			console.error(exception);
			setMsg({ msg: 'Błąd podczas dodawania odbiorcy', severity: 'error' });
		} finally {
			setIsLoading(false);
		}
	};

	const handleClose = () => {
		setSelectedRecipient(null);
		setRecipientType('teacher');
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth='sm'
			fullWidth
		>
			<DialogTitle>Dodaj odbiorcę ręcznie</DialogTitle>
			<DialogContent>
				<Box
					display='flex'
					flexDirection='column'
					gap={2}
					pt={1}
				>
					<FormControl fullWidth>
						<InputLabel>Typ odbiorcy</InputLabel>
						<Select
							value={recipientType}
							label='Typ odbiorcy'
							onChange={(e) => {
								setRecipientType(e.target.value as 'teacher' | 'secretary' | 'student');
								setSelectedRecipient(null);
							}}
						>
							<MenuItem value='teacher'>Nauczyciel</MenuItem>
							<MenuItem value='secretary'>Sekretarz</MenuItem>
							<MenuItem value='student'>Uczeń</MenuItem>
						</Select>
					</FormControl>

					<Autocomplete
						value={selectedRecipient}
						onChange={(event, newValue) => setSelectedRecipient(newValue)}
						options={availableRecipients}
						getOptionLabel={(option) => option.label || ''}
						loading={isLoadingRecipients}
						renderInput={(params) => (
							<TextField
								{...params}
								label='Wybierz odbiorcę'
								InputProps={{
									...params.InputProps,
									endAdornment: (
										<>
											{isLoadingRecipients ? <CircularProgress size={20} /> : null}
											{params.InputProps.endAdornment}
										</>
									),
								}}
							/>
						)}
						noOptionsText='Brak dostępnych odbiorców'
						fullWidth
					/>

					{selectedRecipient && (
						<Alert
							severity='info'
							sx={{ mt: 1 }}
						>
							<Typography variant='caption'>
								<strong>Email:</strong> {selectedRecipient.email}
								<br />
								<strong>Okręg:</strong> {selectedRecipient.district_name || 'N/A'}
							</Typography>
						</Alert>
					)}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={handleClose}
					disabled={isLoading}
				>
					Anuluj
				</Button>
				<Button
					onClick={handleSubmit}
					variant='contained'
					disabled={isLoading || !selectedRecipient}
					startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
				>
					{isLoading ? 'Dodawanie...' : 'Dodaj'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default EmailCampaignRecipientsTable;
